import { type Accessor, createSignal } from "solid-js";

/**
 * §5.2 / §ディレクトリ規律: 30fps で更新される realtime 系 signal を session-store から
 * 隔離する専用モジュール。 import 範囲を「PiP HUD / SessionControls / session-page の onResult」
 * に絞って Solid の fine-grained reactivity の効きを最大化する。
 *
 * このモジュールが扱うのは「signal の更新」 のみ。 永続化用累積 (`marPoints` / `speakingIntervals`)
 * への push は session-store に残す (issue #18: 30fps update を 2 段に分割)。
 *
 * issue #224: HUD 表示を「開口度生値」から「通知発火までの sustain 進捗」に再設計。
 * `sustainProgress` は detection-machine の `openSince` 経過時間 / `sustainMs` を 0..1 に
 * 正規化した値で、 detection-loop が `detectionStep` の結果 state から導出して push する。
 * 旧 `marNormalized` (dynamic-baseline 生値) と旧 `isSpeaking` (vision-VAD 発話判定) signal は
 * HUD 表示からの削除に伴い廃止。 発話時間統計 (UC-14) 用の isSpeaking 累積は session-store の
 * `pushSessionAccumulators` 側で維持される (signal とは独立)。
 *
 * issue #226: HUD の質的表示 (見出し / 配色 / glow) を notifiedActive (通知発火〜cooldown の区間)
 * 駆動に分離し、 定量表示 (進捗バー fill) だけが sustainProgress を使う。 これで threshold 出入りに
 * よる質的表示の点滅 (PRODUCT §1.3 集中阻害) を解消する。 導出式は detection-loop が所有する
 * (sustainProgress と同じ段3)。 本モジュールは lifecycle 非依存の signal holder に留める。
 *
 * lifecycle 無関係に常時更新する設計理由: running 前後の session preview HUD が
 * 固まらないように、 lifecycle ガードはここに入れない。 「running 中だけ永続化したい」 という
 * ドメイン要件は段 2 (session-store の `pushSessionAccumulators`) で表現する。
 */

type RealtimeMetrics = {
  /** 通知発火までの sustain 進捗 (0..1)。 1.0 = sustainMs 到達 = 発火タイミング。 */
  sustainProgress: number;
  /** 通知発火〜cooldown 経過の区間で true (issue #226)。 質的表示 (見出し / 配色 / glow) の driver。 */
  notifiedActive: boolean;
};

const [sustainProgress, setSustainProgress] = createSignal(0);
const [notifiedActive, setNotifiedActive] = createSignal(false);
const [fps, setFps] = createSignal(0);

export const realtimeSustainProgress: Accessor<number> = sustainProgress;
export const realtimeNotifiedActive: Accessor<boolean> = notifiedActive;
export const realtimeFps: Accessor<number> = fps;

export function updateRealtimeSignals(metrics: RealtimeMetrics): void {
  setSustainProgress(metrics.sustainProgress);
  setNotifiedActive(metrics.notifiedActive);
}

export function updateRealtimeFps(next: number): void {
  setFps(next);
}

export function resetRealtimeSignals(): void {
  setSustainProgress(0);
  setNotifiedActive(false);
  setFps(0);
}
